import { createAsyncThunk } from '@reduxjs/toolkit';
import axios, { AxiosResponse } from 'axios';
import { getErrorMessage } from 'utils/errorHandler';
import { setAppRegion } from 'utils/petFormHelpers';
import axiosInstance from '../../services/axiosMiddleware';
import { setAgentId, setAgentNameAndCompany, setApprovedPetsDetails, setFormId, setIsValidAgent, setPrimartTenant, setStageOneData, setTenantDetails, setTenantSubmittedData } from './slice';
import { AgentApprovedApplicationsResponse, GetTenantPetApplicationResponse, IsValidAgentResponse, StageOneDataResponse, StageOneSubmitResponse, SubmitAgentRequestPayload } from './types';


export const createTenant = createAsyncThunk(
    'tenant/createTenant',
    async (tenantData: { name: string; email: string }, { dispatch, rejectWithValue }) => {
        try {
            const response: AxiosResponse<StageOneSubmitResponse> = await axiosInstance.post('/tenant/create-application', tenantData);
            dispatch(setPrimartTenant(response.data))
            return response;
        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                return rejectWithValue(error.response?.data || error.message);
            }
            return rejectWithValue('An unexpected error occurred');
        }
    }
);

export const getTenantDetailsThunk = createAsyncThunk(
    'tenant/getTenantDetails',
        async ({ tenantId, applicationId }: { tenantId: string; applicationId: string }, { dispatch, rejectWithValue }) => {
            try {
                const response: AxiosResponse<StageOneDataResponse> = await axiosInstance.get(`/tenant/application/${tenantId}/${applicationId}`);
                dispatch(setStageOneData(response.data))
                return response.data;
            } catch (error: unknown) {
                if (axios.isAxiosError(error)) {
                    return rejectWithValue(error.response?.data || error.message);
                }
                return rejectWithValue('An unexpected error occurred');
            }
        }
    );


export const submitTenantApplicationThunk = createAsyncThunk(
    'tenant/submitTenantApplicationThunk',
    async (
        { tenantData, applicationId }: { tenantData: any; applicationId: string },
        { dispatch, rejectWithValue }
    ) => {
        try {
            const response: AxiosResponse<StageOneSubmitResponse> = await axiosInstance.post(`/tenant/application/save`, tenantData);
            dispatch(setTenantDetails(response.data))
            return response.data;
        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                return rejectWithValue(error.response?.data || error.message);
            }
            return rejectWithValue('An unexpected error occurred');
        }
    }
);

export const submitTenantPetInfoDataThunk = createAsyncThunk(
    'tenant/submitTenantPetInfoDataThunk',
    async (
        { tenantData, applicationId }: { tenantData: any; applicationId: string },
        { dispatch, rejectWithValue }
    ) => {
        try {
            const response: AxiosResponse<StageOneSubmitResponse> = await axiosInstance.post(`/tenant/save-petinfo`, tenantData);
            dispatch(setTenantDetails(response.data))
            return response.data;
        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                return rejectWithValue(error.response?.data || error.message);
            }
            return rejectWithValue('An unexpected error occurred');
        }
    }
);

export const getTenantPetApplication = createAsyncThunk(
    'agent/getTenantPetApplication',
    async ({ agentId, agentLink }: { agentId: string; agentLink: string }, { dispatch, rejectWithValue }) => {
        try {
            const response: AxiosResponse<GetTenantPetApplicationResponse> = await axiosInstance.get(`/agent/${agentId}/tenantapplication/${agentLink}`);
            dispatch(setTenantSubmittedData(response.data))
            // Set region from API response (property.region) for agent view.
            // property.region is null whenever the property has no region_id, so
            // never clear on absence — that would drop a QLD session back to NSW.
            setAppRegion((response.data as any).data?.property?.region);
            return response.data;
        } catch (error: unknown) {
            return rejectWithValue(getErrorMessage(error));
        }
    }
);

export const verifyIsValidAgent = createAsyncThunk(
    'tenant/verifyIsValidAgent',
    async ({ agentId, region }: { agentId: string; region?: string }, { dispatch, rejectWithValue }) => {
        try {
            const params = region ? { region } : {};
            const response: AxiosResponse<IsValidAgentResponse> = await axiosInstance.get(`/agent/${agentId}`, { params });
            dispatch(setIsValidAgent(response.data.success))
            dispatch(setAgentId(response.data.data?.agent_uuid ?? ""))
            dispatch(setFormId(response.data.data?.form_id ?? ""))
            dispatch(setAgentNameAndCompany({name: response.data.data?.agent_name || "", company: response.data.data?.company_name || "", logo: response.data.data?.agent_logo || ""}))
            return response.data;
        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                return rejectWithValue(error.response?.data || error.message);
            }
            return rejectWithValue('An unexpected error occurred');
        }
    }
);

export const submitTenantResponseThunk = createAsyncThunk(
    'tenant/submitTenantResponseThunk',
    async (
        { agentId, applicationId, agentResponseData }: { agentId: string; applicationId: string, agentResponseData: SubmitAgentRequestPayload },
        { dispatch, rejectWithValue }
    ) => {
        try {
            const response: AxiosResponse<StageOneSubmitResponse> = await axiosInstance.post(`/agent/${agentId}/save-decision/${applicationId}`, agentResponseData);
            dispatch(setTenantDetails(response.data))
            return response;
        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                return rejectWithValue(error.response?.data || error.message);
            }
            return rejectWithValue('An unexpected error occurred');
        }
    }
);

export const savePetDecisionByAgent = createAsyncThunk(
    'tenant/savePetDecisionByAgent',
    async (
        { agentId, applicationId, agentResponseData }: { agentId: string; applicationId: string, agentResponseData: any },
        { dispatch, rejectWithValue }
    ) => {
        try {
            const response: AxiosResponse<StageOneSubmitResponse> = await axiosInstance.post(`/agent/${agentId}/save-pet-decision/${applicationId}`, agentResponseData);
            dispatch(setTenantDetails(response.data))
            return response;
        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                return rejectWithValue(error.response?.data || error.message);
            }
            return rejectWithValue('An unexpected error occurred');
        }
    }
);

export const getAgentApprovedResponse = createAsyncThunk(
    'agent/getAgentApprovedResponse',
    async ({ landLordId }: { landLordId: string; }, { dispatch, rejectWithValue }) => {
        try {
            const response: AxiosResponse<AgentApprovedApplicationsResponse> = await axiosInstance.get(`/landlords/${landLordId}`);
            await dispatch(setApprovedPetsDetails(response.data))
            // Set region from API response (properties[0].property.region) for landlord view.
            // Absent region means "not told", not NSW — see setAppRegion.
            setAppRegion((response.data as any).data?.properties?.[0]?.property?.region);
            return response.data;
        } catch (error: unknown) {
            return rejectWithValue(getErrorMessage(error));
        }
    }
);

export const submitLandLordResponseThunk = createAsyncThunk(
    'tenant/submitLandLordResponseThunk',
    async (
        { landLordId, applicationId, landlord_general_consent, landlord_conditions_discussed }: { landLordId: string; applicationId: string; landlord_general_consent: boolean; landlord_conditions_discussed: boolean },
        { dispatch, rejectWithValue }
    ) => {
        try {
            const response: AxiosResponse<StageOneSubmitResponse> = await axiosInstance.post(`/landlords/${landLordId}/save`, {application_id: applicationId, landlord_general_consent, landlord_conditions_discussed});
            dispatch(setTenantDetails(response.data))
            return response;
        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                return rejectWithValue(error.response?.data || error.message);
            }
            return rejectWithValue('An unexpected error occurred');
        }
    }
);

/**
 * Delete pet data thunk
 */
export const deletePetsData = createAsyncThunk(
  "tenant/deletePetsData",
  async (
    { pet_id }: { pet_id: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.delete(
        `/tenant/pet/${pet_id}`
      );
      return response.data;
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data || error.message);
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);